import { useEffect, useMemo, useState } from 'react';
import {
  Surface,
  cardStyle,
  dangerButtonStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n, type I18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { customersRepoFor } from '../lib/repositories/customers-repo.js';
import { voucherTemplatesRepoFor } from '../lib/repositories/vouchers-repo.js';
import { customerTagsRepoFor } from '../lib/repositories/customer-tags-repo.js';
import {
  subscriptionsByCustomerSnapshot,
  washPackagesRepoFor,
} from '../lib/repositories/wash-packages-repo.js';
import { callIssueVoucher, callSetCustomerTags } from '../lib/callables/loyalty.js';
import {
  callCancelWashSubscription,
  callIssueWashSubscription,
  type IssueWashSubscriptionInput,
} from '../lib/callables/subscriptions.js';
import { logger } from '../lib/logger.js';
import { defaultTiers } from '@kilat/config-seed';
import {
  filterForOutlet,
  money,
  type CustomerSubscription,
  type CustomerTag,
  type TenantCustomerIndex,
  type VoucherTemplate,
  type WashPackage,
  type Locale,
} from '@kilat/shared';

/**
 * /customers — operator's customer book.
 *
 * Powers ad-hoc voucher issuance: until now operators could only issue
 * vouchers from a ticket context (POS). This page lets them say
 * "Mrs Tan comes every Saturday, send her a thank-you" without waiting.
 *
 * Data: `tenants/{tid}/customers/{customerUid}` denormalised index
 * (maintained by `onTicketCollected`). Rules limit reads to owner|manager|cashier.
 *
 * Premium-logical constraint (per repo memory): every state visible — loading,
 * empty (no customers), empty (no search match), error, detail empty,
 * voucher-issued success, all spell out *what's happening* and *what's next*.
 *
 * COST GUARD (Sprint 121 — audit 2026-07-05 §2): this page previously
 * live-subscribed the ENTIRE `customers` collection. It now loads only the
 * `CUSTOMERS_LOAD_CAP` most-recently-active customers (`lastVisitAt desc`),
 * so a page open costs O(cap) reads, not O(all customers). Client-side filter
 * runs over that bounded set; when the cap is reached the header says so
 * (no-dead-ends — never imply you're searching everyone when you aren't).
 * Server-paginated / indexed search ships when an operator outgrows the cap.
 */

const MAX_VISIBLE_ROWS = 50;
/** Most-recent customers loaded into the live listener. Bounds read cost + memory. */
const CUSTOMERS_LOAD_CAP = 500;

export function CustomersPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const customersSub = useMemo(() => {
    if (!tenantId) return null;
    const repo = customersRepoFor(tenantId);
    return (cb: (vs: TenantCustomerIndex[]) => void) =>
      repo.listSnapshotBounded(
        { orderBy: [['lastVisitAt', 'desc']], limitTo: CUSTOMERS_LOAD_CAP },
        cb,
      );
  }, [tenantId]);

  const {
    data: customers,
    loading,
    error,
  } = useTrackedSnapshot<TenantCustomerIndex[]>(['customers', tenantId], customersSub);

  const templatesSub = useMemo(() => {
    if (!tenantId) return null;
    const repo = voucherTemplatesRepoFor(tenantId);
    return (cb: (vs: VoucherTemplate[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const { data: templates } = useTrackedSnapshot<VoucherTemplate[]>(
    ['voucher-templates', tenantId],
    templatesSub,
  );

  const tagsSub = useMemo(() => {
    if (!tenantId) return null;
    const repo = customerTagsRepoFor(tenantId);
    return (cb: (vs: CustomerTag[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const { data: tags } = useTrackedSnapshot<CustomerTag[]>(['customer-tags', tenantId], tagsSub);

  const [query, setQuery] = useState('');
  const [selectedUid, setSelectedUid] = useState<string | null>(null);

  if (!tenantId) {
    return (
      <DeadEndScreen
        title={t.t('dashboard.customers.noTenantTitle')}
        body={t.t('dashboard.customers.noTenantBody')}
        cta={{ label: t.t('dashboard.states.backToQueue'), to: '/queue' }}
      />
    );
  }

  const list = customers ?? [];
  const filtered = filterCustomers(list, query).slice(0, MAX_VISIBLE_ROWS);
  const selected = selectedUid ? (list.find((c) => c.customerUid === selectedUid) ?? null) : null;

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.customers.title')}
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 0,
            }}
          >
            {loading
              ? t.t('common.loading')
              : list.length >= CUSTOMERS_LOAD_CAP
                ? t.t('dashboard.customers.summaryCapped', {
                    cap: CUSTOMERS_LOAD_CAP,
                    shown: filtered.length,
                  })
                : t.t('dashboard.customers.summaryKnown', {
                    known: list.length,
                    shown: filtered.length,
                  })}
          </p>
        </div>
        <Link to="/queue" style={ghostLinkStyle}>
          {t.t('dashboard.states.backToQueue')}
        </Link>
      </header>

      <div className="layout-split-aside">
        <Surface as="section">
          <SearchBar value={query} onChange={setQuery} />

          <div style={{ marginTop: 'var(--space-4)' }}>
            {loading ? (
              <InlineNote>{t.t('dashboard.customers.loadingLive')}</InlineNote>
            ) : error ? (
              <InlineNote tone="error">
                {t.t('dashboard.customers.loadError', {
                  message: String(error.message ?? error),
                })}
              </InlineNote>
            ) : list.length === 0 ? (
              <EmptyState
                title={t.t('dashboard.customers.emptyNoCustomersTitle')}
                body={t.t('dashboard.customers.emptyNoCustomersBody')}
              />
            ) : filtered.length === 0 ? (
              <EmptyState
                title={t.t('dashboard.customers.emptyNoMatchTitle', { query })}
                body={t.t('dashboard.customers.emptyNoMatchBody')}
                cta={{ label: t.t('dashboard.customers.clearSearch'), onClick: () => setQuery('') }}
              />
            ) : (
              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'grid',
                  gap: 'var(--space-2)',
                }}
              >
                {filtered.map((c) => (
                  <li key={c.customerUid}>
                    <CustomerRow
                      customer={c}
                      selected={c.customerUid === selectedUid}
                      onSelect={() => setSelectedUid(c.customerUid)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Surface>

        <section style={{ ...cardStyle, position: 'sticky', top: 'var(--space-4)' }}>
          {selected ? (
            <CustomerDetail
              tenantId={tenantId}
              customer={selected}
              templates={templates ?? []}
              tags={tags ?? []}
            />
          ) : (
            <EmptyState
              title={t.t('dashboard.customers.selectTitle')}
              body={t.t('dashboard.customers.selectBody')}
            />
          )}
        </section>
      </div>
    </main>
  );
}

// ── Search ─────────────────────────────────────────────────────────────

function SearchBar({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (v: string) => void;
}) {
  const t = useI18n();
  return (
    <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <span style={fieldLabel}>{t.t('dashboard.customers.searchLabel')}</span>
      <div style={{ position: 'relative' }}>
        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t.t('dashboard.customers.searchPlaceholder')}
          autoComplete="off"
          autoFocus
          style={{ ...inputStyle, paddingRight: value ? 76 : 14 }}
        />
        {value ? (
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label={t.t('dashboard.customers.clearSearch')}
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              padding: '4px 10px',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              background: 'transparent',
              border: '1px solid var(--color-border-strong)',
              borderRadius: 'var(--radius-pill)',
              cursor: 'pointer',
            }}
          >
            {t.t('dashboard.customers.clear')}
          </button>
        ) : null}
      </div>
      <span
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t.t('dashboard.customers.searchHint')}
      </span>
    </label>
  );
}

// ── List row ───────────────────────────────────────────────────────────

function CustomerRow({
  customer,
  selected,
  onSelect,
}: {
  readonly customer: TenantCustomerIndex;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  const t = useI18n();
  const tier = tierMeta(customer.tierId, t.locale);
  const lastVisitLabel = formatRelativeFromMs(t, timestampToMs(customer.lastVisitAt));

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        width: '100%',
        textAlign: 'left',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: selected ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
        background: selected ? 'rgba(13,122,79,0.04)' : 'var(--color-surface)',
        cursor: 'pointer',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'baseline',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontWeight: 500 }}>{customer.displayName}</span>
          {customer.platesLower.length > 0 ? (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
              {customer.platesLower.map((p) => p.toUpperCase()).join(' · ')}
            </span>
          ) : null}
        </div>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t(
            customer.visits === 1
              ? 'dashboard.customers.rowMetaOne'
              : 'dashboard.customers.rowMetaOther',
            {
              visits: customer.visits,
              spend: money.format(customer.lifetimeSpendSen),
              lastSeen: lastVisitLabel,
            },
          )}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <TierBadge tier={tier} />
      </div>
    </button>
  );
}

// ── Detail panel ──────────────────────────────────────────────────────

function CustomerDetail({
  tenantId,
  customer,
  templates,
  tags,
}: {
  readonly tenantId: string;
  readonly customer: TenantCustomerIndex;
  readonly templates: readonly VoucherTemplate[];
  readonly tags: readonly CustomerTag[];
}) {
  const t = useI18n();
  const tier = tierMeta(customer.tierId, t.locale);
  const firstVisit = formatDateFromMs(timestampToMs(customer.firstVisitAt));
  const lastVisit = formatDateFromMs(timestampToMs(customer.lastVisitAt));

  const activeTemplates = useMemo(() => templates.filter(isUsableTemplate), [templates]);

  const [pickedTemplateId, setPickedTemplateId] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'issuing' | 'issued' | 'error'>('idle');
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastIssuedName, setLastIssuedName] = useState<string | null>(null);

  // Tag state — tracks what's persisted (customer.tags) AND any local pending
  // change while the operator is editing. Local state lets us show optimistic
  // updates immediately while the callable round-trips.
  const persistedTagIds = customer.tags ?? [];
  const [tagDraftIds, setTagDraftIds] = useState<Set<string> | null>(null);
  const [tagStatus, setTagStatus] = useState<'idle' | 'saving' | 'error'>('idle');
  const [tagError, setTagError] = useState<string | null>(null);
  const activeTagsById = useMemo(
    () => Object.fromEntries(tags.filter((tg) => !tg.archived).map((tg) => [tg.id, tg])),
    [tags],
  );
  const allTagsById = useMemo(() => Object.fromEntries(tags.map((tg) => [tg.id, tg])), [tags]);

  // Reset the draft whenever the selected customer changes.
  useEffect(() => {
    setTagDraftIds(null);
    setTagStatus('idle');
    setTagError(null);
  }, [customer.customerUid]);

  const editingTags = tagDraftIds !== null;
  const draftSet = tagDraftIds ?? new Set(persistedTagIds);

  function toggleDraftTag(id: string) {
    const next = new Set(draftSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setTagDraftIds(next);
  }

  async function saveTags() {
    if (!tagDraftIds) return;
    setTagStatus('saving');
    setTagError(null);
    try {
      const result = await callSetCustomerTags({
        tenantId,
        customerUid: customer.customerUid,
        tagIds: Array.from(tagDraftIds),
      });
      if (result.droppedTagIds.length > 0) {
        logger.info('setCustomerTags dropped some ids', { dropped: result.droppedTagIds });
      }
      setTagStatus('idle');
      setTagDraftIds(null);
    } catch (e) {
      logger.error('setCustomerTags failed', { error: String(e) });
      setTagError(extractFriendlyError(t, e));
      setTagStatus('error');
    }
  }

  async function issue(templateId: string) {
    setStatus('issuing');
    setLastError(null);
    try {
      await callIssueVoucher({
        tenantId,
        templateId,
        customerUid: customer.customerUid,
      });
      const name =
        templates.find((tmpl) => tmpl.id === templateId)?.name['en'] ??
        t.t('dashboard.customers.voucherFallback');
      setLastIssuedName(name);
      setStatus('issued');
      setPickedTemplateId(null);
    } catch (e) {
      logger.error('issueVoucher from /customers failed', { templateId, error: String(e) });
      setLastError(extractFriendlyError(t, e));
      setStatus('error');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      <header>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.customers.customerEyebrow')}
        </p>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'baseline',
            marginTop: 'var(--space-1)',
          }}
        >
          <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 600, margin: 0 }}>
            {customer.displayName}
          </h2>
          <TierBadge tier={tier} />
        </div>
        <p
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {customer.platesLower.length > 0
            ? `${customer.platesLower.map((p) => p.toUpperCase()).join(' · ')}`
            : t.t('dashboard.customers.noPlate')}
          {customer.phoneNormalised ? ` · ${formatPhone(customer.phoneNormalised)}` : ''}
        </p>
      </header>

      <dl style={statGrid}>
        <Stat
          label={t.t('dashboard.customers.statLifetimeSpend')}
          value={money.format(customer.lifetimeSpendSen)}
        />
        <Stat
          label={t.t('dashboard.customers.statLifetimePoints')}
          value={String(customer.lifetimePoints)}
        />
        <Stat label={t.t('dashboard.customers.statVisits')} value={String(customer.visits)} />
        <Stat label={t.t('dashboard.customers.statFirstVisit')} value={firstVisit} />
        <Stat label={t.t('dashboard.customers.statLastVisit')} value={lastVisit} />
        <Stat label={t.t('dashboard.customers.statTier')} value={tier.label} />
      </dl>

      {/* Tag section — every state has an explicit next-CTA */}
      <section
        style={{
          padding: 'var(--space-4)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {t.t('dashboard.customers.tagsHeading')}
          </h3>
          {!editingTags && Object.keys(activeTagsById).length > 0 ? (
            <button
              type="button"
              onClick={() => setTagDraftIds(new Set(persistedTagIds))}
              style={tinyGhostButtonStyle}
            >
              {persistedTagIds.length > 0
                ? t.t('dashboard.customers.editTags')
                : t.t('dashboard.customers.addTag')}
            </button>
          ) : null}
        </div>

        {Object.keys(allTagsById).length === 0 ? (
          <p
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('dashboard.customers.noTagsExistPre')}
            <Link to="/loyalty/tags" style={{ color: 'var(--color-primary)' }}>
              {t.t('dashboard.customers.createOne')}
            </Link>
            {t.t('dashboard.customers.noTagsExistPost')}
          </p>
        ) : editingTags ? (
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-3)' }}>
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t.t('dashboard.customers.tagEditHint')}
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {Object.values(activeTagsById).map((tg) => {
                const on = draftSet.has(tg.id);
                return (
                  <button
                    key={tg.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleDraftTag(tg.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 'var(--radius-pill)',
                      border: `1px solid ${on ? (tg.colour ?? 'var(--color-primary)') : 'var(--color-border-strong)'}`,
                      background: on
                        ? tg.colour
                          ? `${tg.colour}26`
                          : 'rgba(13,122,79,0.10)'
                        : 'transparent',
                      color: on ? (tg.colour ?? 'var(--color-primary)') : 'var(--color-on-surface)',
                      fontSize: 'var(--text-meta)',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    {tg.label['en'] ?? tg.id}
                    {on ? ' ✓' : ''}
                  </button>
                );
              })}
            </div>
            {tagStatus === 'error' && tagError ? (
              <p
                role="alert"
                style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
              >
                {tagError}
              </p>
            ) : null}
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <button
                type="button"
                onClick={() => void saveTags()}
                disabled={tagStatus === 'saving'}
                style={{
                  ...primaryButtonStyle,
                  opacity: tagStatus === 'saving' ? 0.6 : 1,
                  padding: '8px 14px',
                }}
              >
                {tagStatus === 'saving'
                  ? t.t('dashboard.customers.saving')
                  : t.t(
                      draftSet.size === 1
                        ? 'dashboard.customers.saveTagsOne'
                        : 'dashboard.customers.saveTagsOther',
                      { count: draftSet.size },
                    )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setTagDraftIds(null);
                  setTagError(null);
                  setTagStatus('idle');
                }}
                disabled={tagStatus === 'saving'}
                style={tinyGhostButtonStyle}
              >
                {t.t('common.cancel')}
              </button>
            </div>
          </div>
        ) : persistedTagIds.length === 0 ? (
          <p
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('dashboard.customers.noTagsOnCustomer')}
          </p>
        ) : (
          <div
            style={{
              marginTop: 'var(--space-2)',
              display: 'flex',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            {persistedTagIds.map((id) => {
              const tg = allTagsById[id];
              if (!tg) return null;
              return (
                <span
                  key={id}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-pill)',
                    background: tg.colour ? `${tg.colour}1f` : 'var(--color-border)',
                    color: tg.colour ?? 'var(--color-on-surface-muted)',
                    fontSize: 'var(--text-meta)',
                    fontWeight: 500,
                  }}
                >
                  {tg.label['en'] ?? tg.id}
                </span>
              );
            })}
          </div>
        )}
      </section>

      {/* Wash subscriptions (Sprint 96). Active packs visible at-a-glance;
          Issue modal lets the operator sell a new pack on the spot. */}
      <SubscriptionsSection tenantId={tenantId} customer={customer} />

      {/* Issue-voucher panel. Has all four states: empty, idle, issuing, issued+next-CTA, error. */}
      <section
        style={{
          padding: 'var(--space-4)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          background: 'rgba(13,122,79,0.04)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t.t('dashboard.customers.issueVoucherHeading')}
        </h3>

        {status === 'issued' && lastIssuedName ? (
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-3)' }}>
            <p role="status" style={{ margin: 0, color: 'var(--color-primary)', fontWeight: 500 }}>
              {t.t('dashboard.customers.voucherIssued', {
                name: lastIssuedName,
                customer: customer.displayName,
              })}
            </p>
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t.t('dashboard.customers.voucherIssuedNote')}
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => {
                  setStatus('idle');
                  setLastIssuedName(null);
                }}
                style={primaryButtonStyle}
              >
                {t.t('dashboard.customers.issueAnother')}
              </button>
            </div>
          </div>
        ) : activeTemplates.length === 0 ? (
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-3)' }}>
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t.t('dashboard.customers.noActiveTemplates')}
            </p>
            <Link
              to="/loyalty/vouchers"
              style={{
                ...primaryButtonStyle,
                display: 'inline-flex',
                justifyContent: 'center',
                textDecoration: 'none',
              }}
            >
              {t.t('dashboard.customers.createTemplate')}
            </Link>
          </div>
        ) : (
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-3)' }}>
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t.t('dashboard.customers.pickTemplateHint')}
            </p>
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'grid',
                gap: 'var(--space-2)',
              }}
            >
              {activeTemplates.map((tmpl) => (
                <li key={tmpl.id}>
                  <TemplatePickerRow
                    template={tmpl}
                    selected={pickedTemplateId === tmpl.id}
                    onSelect={() => setPickedTemplateId(tmpl.id)}
                  />
                </li>
              ))}
            </ul>

            {status === 'error' && lastError ? (
              <p
                role="alert"
                style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
              >
                {lastError}
              </p>
            ) : null}

            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={!pickedTemplateId || status === 'issuing'}
                onClick={() => pickedTemplateId && void issue(pickedTemplateId)}
                style={{
                  ...primaryButtonStyle,
                  opacity: !pickedTemplateId || status === 'issuing' ? 0.5 : 1,
                  cursor: !pickedTemplateId || status === 'issuing' ? 'not-allowed' : 'pointer',
                }}
              >
                {status === 'issuing'
                  ? t.t('dashboard.customers.issuing')
                  : pickedTemplateId
                    ? t.t('dashboard.customers.issueThisVoucher')
                    : t.t('dashboard.customers.pickTemplateAbove')}
              </button>
              <Link to="/loyalty/vouchers" style={ghostLinkStyle}>
                {t.t('dashboard.customers.manageTemplates')}
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function TemplatePickerRow({
  template,
  selected,
  onSelect,
}: {
  readonly template: VoucherTemplate;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  const t = useI18n();
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: selected
          ? '1px solid var(--color-primary)'
          : '1px solid var(--color-border-strong)',
        background: selected ? 'rgba(13,122,79,0.06)' : 'var(--color-surface)',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
        }}
      >
        <span style={{ fontWeight: 500 }}>{template.name['en'] ?? template.id}</span>
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {describeEffect(t, template.effect)}
        </span>
      </div>
    </button>
  );
}

// ── Wash subscriptions section (Sprint 96) ─────────────────────────────

/**
 * Customer-detail subscriptions panel.
 *
 * Live-reads the customer's active wash packs for this tenant via the
 * filtered `subscriptionsByCustomerSnapshot` (server-side `where`
 * clause — not a full collection scan). Every state explicit per the
 * no-dead-ends rule:
 *   - loading: skeleton hint
 *   - none-active + no-issue-yet: explainer + "Issue a pack" CTA
 *   - active: card list with remaining + expiry + cancel
 *   - cancel error: inline message + retry CTA
 *
 * "Issue" opens a modal that lets the operator pick outlet + package +
 * payment method and confirm. The modal manages its own busy/error
 * state; on success it closes and the live listener picks up the new
 * subscription within ~250ms.
 */
function SubscriptionsSection({
  tenantId,
  customer,
}: {
  readonly tenantId: string;
  readonly customer: TenantCustomerIndex;
}) {
  const t = useI18n();
  const [subs, setSubs] = useState<CustomerSubscription[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setSubs(null);
    setLoadError(null);
    const unsub = subscriptionsByCustomerSnapshot(
      tenantId,
      customer.customerUid,
      (list) => {
        setSubs(list);
        setLoadError(null); // recovery — clear stale error on a successful snapshot
      },
      (err) => {
        logger.error('subscriptionsByCustomerSnapshot failed', {
          tenantId,
          customerUid: customer.customerUid,
          error: String(err),
        });
        setLoadError(extractFriendlySubscriptionError(t, err));
      },
    );
    return () => unsub();
    // `t` is intentionally omitted from deps: re-subscribing the Firestore
    // listener on a locale switch would cause a needless load flash. The
    // friendly-error copy resolves against the current locale on next error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, customer.customerUid, reloadTick]);

  const active = useMemo(
    () =>
      (subs ?? [])
        .filter((s) => isLiveSubscription(s))
        .sort((a, b) => timestampToMs(b.issuedAt) - timestampToMs(a.issuedAt)),
    [subs],
  );
  const archived = useMemo(
    () =>
      (subs ?? [])
        .filter((s) => !isLiveSubscription(s))
        .sort((a, b) => timestampToMs(b.issuedAt) - timestampToMs(a.issuedAt)),
    [subs],
  );

  return (
    <section
      style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t.t('dashboard.customers.washSubsHeading')}
        </h3>
        <button type="button" onClick={() => setIssueOpen(true)} style={tinyGhostButtonStyle}>
          {t.t('dashboard.customers.issuePack')}
        </button>
      </div>

      {loadError ? (
        <div
          role="alert"
          style={{
            marginTop: 'var(--space-3)',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.20)',
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          <p style={{ margin: 0, fontSize: 'var(--text-meta)', color: '#991b1b' }}>
            {t.t('dashboard.customers.washPacksLoadError', { error: loadError })}
          </p>
          <div>
            <button
              type="button"
              onClick={() => setReloadTick((n) => n + 1)}
              style={tinyGhostButtonStyle}
            >
              {t.t('dashboard.customers.retry')}
            </button>
          </div>
        </div>
      ) : subs === null ? (
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.customers.loadingPacks')}
        </p>
      ) : active.length === 0 && archived.length === 0 ? (
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.customers.noSubscriptionYet')}
        </p>
      ) : (
        <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
          {active.length === 0 ? (
            <p
              style={{
                margin: 0,
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--tint-subtle)',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t.t('dashboard.customers.noActivePacks')}
            </p>
          ) : null}
          {active.map((s) => (
            <SubscriptionRow
              key={s.id}
              tenantId={tenantId}
              subscription={s}
              onCancelError={(msg) => setCancelError(msg)}
            />
          ))}
          {archived.length > 0 ? (
            <details style={{ marginTop: 'var(--space-2)' }}>
              <summary
                style={{
                  cursor: 'pointer',
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                }}
              >
                {t.t(
                  archived.length === 1
                    ? 'dashboard.customers.pastSubsOne'
                    : 'dashboard.customers.pastSubsOther',
                  { count: archived.length },
                )}
              </summary>
              <div style={{ marginTop: 'var(--space-2)', display: 'grid', gap: 'var(--space-2)' }}>
                {archived.map((s) => (
                  <SubscriptionRow
                    key={s.id}
                    tenantId={tenantId}
                    subscription={s}
                    onCancelError={(msg) => setCancelError(msg)}
                    inactive
                  />
                ))}
              </div>
            </details>
          ) : null}
        </div>
      )}

      {cancelError ? (
        <p
          role="alert"
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-accent)',
          }}
        >
          {cancelError}{' '}
          <button
            type="button"
            onClick={() => setCancelError(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            {t.t('dashboard.customers.dismiss')}
          </button>
        </p>
      ) : null}

      {issueOpen ? (
        <IssueSubscriptionModal
          tenantId={tenantId}
          customer={customer}
          onClose={() => setIssueOpen(false)}
        />
      ) : null}
    </section>
  );
}

function SubscriptionRow({
  tenantId,
  subscription,
  onCancelError,
  inactive,
}: {
  readonly tenantId: string;
  readonly subscription: CustomerSubscription;
  readonly onCancelError: (msg: string) => void;
  readonly inactive?: boolean;
}) {
  const t = useI18n();
  const [cancelling, setCancelling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState('');
  const remaining =
    subscription.packageSnapshot.kind === 'unlimited'
      ? t.t('dashboard.customers.remainingUnlimited')
      : t.t('dashboard.customers.remainingCount', {
          remaining: subscription.washesRemaining,
          total: subscription.packageSnapshot.washCount ?? 0,
        });
  const expiry = timestampToMs(subscription.expiresAt);
  const expiryLabel = formatDateFromMs(expiry);
  const expiringSoon =
    expiry > 0 &&
    expiry - Date.now() < 14 * 24 * 60 * 60 * 1000 &&
    subscription.status === 'active';

  // Native window.prompt is banned (ADR-0012 — it breaks the premium /
  // native-feel PWA). The "Cancel pack" button opens an in-app modal
  // (CancelPackModal) that captures the optional audit reason; this
  // runs once the operator confirms in that modal.
  async function runCancel() {
    if (cancelling) return;
    setCancelling(true);
    try {
      const trimmed = reason.trim();
      await callCancelWashSubscription({
        tenantId,
        subscriptionId: subscription.id,
        ...(trimmed ? { reason: trimmed } : {}),
      });
      logger.info('subscription cancelled', { subId: subscription.id });
      setConfirmOpen(false);
      setReason('');
    } catch (e) {
      logger.error('cancel subscription failed', { subId: subscription.id, error: String(e) });
      onCancelError(extractFriendlySubscriptionError(t, e));
      // Close the modal so the section-level error alert is visible.
      setConfirmOpen(false);
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div
      style={{
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        background: inactive ? 'var(--tint-subtle)' : 'var(--color-surface)',
        opacity: inactive ? 0.75 : 1,
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontWeight: 500 }}>
          {subscription.packageSnapshot.name['en'] ?? t.t('dashboard.customers.washPackFallback')}
        </span>
        <SubscriptionStatusBadge status={subscription.status} expiringSoon={expiringSoon} />
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t.t('dashboard.customers.subRowMeta', {
          remaining,
          expiry: expiryLabel,
          price: money.format(subscription.pricePaidSen),
          method: paymentMethodLabel(t, subscription.paymentMethod),
        })}
      </p>
      {!inactive && subscription.status === 'active' ? (
        <div>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={cancelling}
            style={{ ...tinyGhostButtonStyle, opacity: cancelling ? 0.6 : 1 }}
          >
            {cancelling
              ? t.t('dashboard.customers.cancelling')
              : t.t('dashboard.customers.cancelPack')}
          </button>
        </div>
      ) : null}

      {confirmOpen ? (
        <CancelPackModal
          packName={
            subscription.packageSnapshot.name['en'] ?? t.t('dashboard.customers.washPackFallback')
          }
          reason={reason}
          onReasonChange={setReason}
          cancelling={cancelling}
          onConfirm={() => void runCancel()}
          onClose={() => {
            // Don't dismiss mid-cancel; the in-flight callable owns the modal.
            if (!cancelling) setConfirmOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Confirm-cancel dialog for a customer's active wash pack. Replaces the
 * old `window.prompt` (banned by ADR-0012) with an in-app modal that
 * mirrors `IssueSubscriptionModal`: same `ModalShell` (Esc + backdrop
 * close, focus target), design tokens, and i18n-only copy. The reason
 * textarea is optional — an empty note omits `reason` from the callable,
 * matching the previous prompt semantics exactly.
 */
function CancelPackModal({
  packName,
  reason,
  onReasonChange,
  cancelling,
  onConfirm,
  onClose,
}: {
  readonly packName: string;
  readonly reason: string;
  readonly onReasonChange: (value: string) => void;
  readonly cancelling: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}) {
  const t = useI18n();
  return (
    <ModalShell title={t.t('dashboard.customers.cancelPackTitle')} onClose={onClose}>
      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.customers.cancelPackBody', { pack: packName })}
        </p>

        <label style={{ display: 'grid', gap: 4 }}>
          <span style={fieldLabel}>{t.t('dashboard.customers.cancelPackReasonLabel')}</span>
          <textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder={t.t('dashboard.customers.cancelPackReasonPlaceholder')}
            rows={3}
            disabled={cancelling}
            autoFocus
            style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
          />
        </label>

        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            justifyContent: 'flex-end',
            marginTop: 'var(--space-2)',
          }}
        >
          <button type="button" onClick={onClose} disabled={cancelling} style={ghostButtonStyle}>
            {t.t('dashboard.customers.keepPack')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={cancelling}
            style={{ ...dangerButtonStyle, opacity: cancelling ? 0.6 : 1 }}
          >
            {cancelling
              ? t.t('dashboard.customers.cancelling')
              : t.t('dashboard.customers.cancelPackConfirm')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function SubscriptionStatusBadge({
  status,
  expiringSoon,
}: {
  readonly status: CustomerSubscription['status'];
  readonly expiringSoon: boolean;
}) {
  const t = useI18n();
  const { bg, fg, label } = (() => {
    if (status === 'active' && expiringSoon) {
      return {
        bg: 'rgba(245,158,11,0.12)',
        fg: '#b45309',
        label: t.t('dashboard.customers.statusExpiringSoon'),
      };
    }
    switch (status) {
      case 'active':
        return {
          bg: 'rgba(13,122,79,0.10)',
          fg: 'var(--color-primary)',
          label: t.t('dashboard.customers.statusActive'),
        };
      case 'expired':
        return {
          bg: 'var(--color-border)',
          fg: 'var(--color-on-surface-muted)',
          label: t.t('dashboard.customers.statusExpired'),
        };
      case 'exhausted':
        return {
          bg: 'var(--color-border)',
          fg: 'var(--color-on-surface-muted)',
          label: t.t('dashboard.customers.statusExhausted'),
        };
      case 'cancelled':
        return {
          bg: 'var(--color-border)',
          fg: 'var(--color-on-surface-muted)',
          label: t.t('dashboard.customers.statusCancelled'),
        };
      case 'paused':
        return {
          bg: 'rgba(59,130,246,0.10)',
          fg: '#1d4ed8',
          label: t.t('dashboard.customers.statusPaused'),
        };
    }
  })();
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: bg,
        color: fg,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

/**
 * Operator-facing Issue-pack modal.
 *
 * Inputs the operator picks:
 *   - Outlet (defaults to the active one from <ActiveOutletProvider>;
 *     can switch if the tenant has multiple).
 *   - Package (live from tenant-library `washPackagesRepoFor(tenantId)`,
 *     filtered to `active === true` AND visible-at-this-outlet via
 *     `filterForOutlet` — Sprint 103 tenant-library migration).
 *   - Payment method (cash / DuitNow QR / TNG / GrabPay / card / bank
 *     transfer / voucher / free) — same enum as the POS.
 *   - Optional price-override in RM (left blank → full package price).
 *
 * Validations stay client-side first, then the callable re-validates.
 * `paymentRef` is omitted in MVP (mostly meaningful for FPX/QR
 * receipts which we don't capture from the dashboard yet).
 */
function IssueSubscriptionModal({
  tenantId,
  customer,
  onClose,
}: {
  readonly tenantId: string;
  readonly customer: TenantCustomerIndex;
  readonly onClose: () => void;
}) {
  const t = useI18n();
  const { outlets, outletId: defaultOutletId, loading: outletsLoading } = useActiveOutlet();
  const [outletId, setOutletId] = useState<string | null>(defaultOutletId);
  const [packages, setPackages] = useState<WashPackage[] | null>(null);
  const [packagesError, setPackagesError] = useState<string | null>(null);
  const [packagesReloadTick, setPackagesReloadTick] = useState(0);
  const [packageId, setPackageId] = useState<string | null>(null);
  const [method, setMethod] = useState<IssueWashSubscriptionInput['paymentMethod']>('cash');
  const [priceRm, setPriceRm] = useState('');
  const [status, setStatus] = useState<'idle' | 'issuing' | 'issued' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Sync local outletId once the ActiveOutletProvider resolves — fixes
  // the dead-end where opening the modal before the provider resolves
  // would leave outletId permanently null (useState only captures the
  // first value of defaultOutletId). Also auto-recover if the selected
  // outlet disappears from the outlets list.
  useEffect(() => {
    if (!outletId && defaultOutletId) {
      setOutletId(defaultOutletId);
      return;
    }
    const firstOutlet = outlets[0];
    if (!outletId && firstOutlet) {
      setOutletId(firstOutlet.id);
      return;
    }
    if (outletId && firstOutlet && !outlets.some((o) => o.id === outletId)) {
      // Selected outlet was removed mid-modal — fall back to the first available.
      setOutletId(firstOutlet.id);
    }
  }, [defaultOutletId, outlets, outletId]);

  useEffect(() => {
    setPackages(null);
    setPackagesError(null);
    setPackageId(null);
    if (!outletId) return;
    const unsub = washPackagesRepoFor(tenantId).listSnapshot(
      (list) => {
        // Sprint 103 — wash packs are tenant-scoped; filter to active +
        // visible-at-this-outlet via the shared `filterForOutlet` helper.
        const visibleHere = filterForOutlet(list, outletId);
        const active = visibleHere.filter((p) => p.active !== false);
        active.sort((a, b) => a.rank - b.rank);
        setPackages(active);
        setPackagesError(null); // recovery
        // Auto-pick the first one to remove a dead-end if there's only one.
        if (active[0] && !packageId) setPackageId(active[0].id);
      },
      (err) => {
        logger.error('washPackages listSnapshot failed', {
          tenantId,
          outletId,
          error: String(err),
        });
        setPackagesError(extractFriendlySubscriptionError(t, err));
      },
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, outletId, packagesReloadTick]);

  const picked = useMemo(
    () => (packages ?? []).find((p) => p.id === packageId) ?? null,
    [packages, packageId],
  );
  const defaultPriceSen = picked?.priceSen ?? 0;
  const priceOverrideSen = parseRmToSenOrNull(priceRm);
  const finalPriceSen = priceOverrideSen ?? defaultPriceSen;
  const priceLooksValid = priceRm.trim() === '' || priceOverrideSen !== null;

  async function confirm() {
    if (!outletId || !packageId) {
      setError(t.t('dashboard.customers.errPickOutletPackage'));
      setStatus('error');
      return;
    }
    if (!priceLooksValid) {
      setError(t.t('dashboard.customers.errOverridePrice'));
      setStatus('error');
      return;
    }
    setStatus('issuing');
    setError(null);
    try {
      await callIssueWashSubscription({
        tenantId,
        outletId,
        packageId,
        customerUid: customer.customerUid,
        paymentMethod: method,
        ...(priceOverrideSen !== null ? { pricePaidSen: priceOverrideSen } : {}),
      });
      logger.info('subscription issued from /customers', {
        tenantId,
        customerUid: customer.customerUid,
        packageId,
      });
      setStatus('issued');
      // Auto-close after a moment so the operator sees the confirmation,
      // then the listener-driven list updates underneath.
      window.setTimeout(() => onClose(), 900);
    } catch (e) {
      logger.error('issueWashSubscription failed', { error: String(e) });
      setError(extractFriendlySubscriptionError(t, e));
      setStatus('error');
    }
  }

  return (
    <ModalShell
      title={t.t('dashboard.customers.issuePackTo', { customer: customer.displayName })}
      onClose={onClose}
    >
      {outletsLoading && outlets.length === 0 ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.customers.loadingOutlets')}
        </p>
      ) : outlets.length === 0 ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('dashboard.customers.needOutlet')}
          </p>
          <Link
            to="/outlets"
            style={{ ...primaryButtonStyle, textAlign: 'center', textDecoration: 'none' }}
          >
            {t.t('dashboard.customers.addOutlet')}
          </Link>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {outlets.length > 1 ? (
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={fieldLabel}>{t.t('dashboard.customers.outletLabel')}</span>
              <select
                value={outletId ?? ''}
                onChange={(e) => setOutletId(e.target.value)}
                style={inputStyle}
              >
                {outlets.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {packagesError ? (
            <div
              role="alert"
              style={{
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(239,68,68,0.06)',
                border: '1px solid rgba(239,68,68,0.20)',
                display: 'grid',
                gap: 'var(--space-2)',
              }}
            >
              <p style={{ margin: 0, fontSize: 'var(--text-meta)', color: '#991b1b' }}>
                {t.t('dashboard.customers.packsLoadError', { error: packagesError })}
              </p>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <button
                  type="button"
                  onClick={() => setPackagesReloadTick((n) => n + 1)}
                  style={tinyGhostButtonStyle}
                >
                  {t.t('dashboard.customers.retry')}
                </button>
                <button type="button" onClick={onClose} style={tinyGhostButtonStyle}>
                  {t.t('dashboard.customers.close')}
                </button>
              </div>
            </div>
          ) : !outletId ? (
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t.t('dashboard.customers.pickOutletAbove')}
            </p>
          ) : packages === null ? (
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t.t('dashboard.customers.loadingPacksOutlet')}
            </p>
          ) : packages.length === 0 ? (
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                }}
              >
                {t.t('dashboard.customers.noPacksConfigured')}
              </p>
              <Link
                to="/loyalty/packages"
                style={{ ...primaryButtonStyle, textAlign: 'center', textDecoration: 'none' }}
              >
                {t.t('dashboard.customers.setupWashPacks')}
              </Link>
            </div>
          ) : (
            <>
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={fieldLabel}>{t.t('dashboard.customers.packLabel')}</span>
                <select
                  value={packageId ?? ''}
                  onChange={(e) => setPackageId(e.target.value)}
                  style={inputStyle}
                >
                  {packages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name['en'] ?? p.id} · {describePackage(t, p)} · {money.format(p.priceSen)}
                    </option>
                  ))}
                </select>
                {picked?.description?.['en'] ? (
                  <span
                    style={{
                      fontSize: 'var(--text-meta)',
                      color: 'var(--color-on-surface-muted)',
                    }}
                  >
                    {picked.description['en']}
                  </span>
                ) : null}
              </label>

              <label style={{ display: 'grid', gap: 4 }}>
                <span style={fieldLabel}>{t.t('dashboard.customers.paymentMethodField')}</span>
                <select
                  value={method}
                  onChange={(e) =>
                    setMethod(e.target.value as IssueWashSubscriptionInput['paymentMethod'])
                  }
                  style={inputStyle}
                >
                  <option value="cash">{t.t('dashboard.customers.paymentMethods.cash')}</option>
                  <option value="duitnow_qr">
                    {t.t('dashboard.customers.paymentMethods.duitnow_qr')}
                  </option>
                  <option value="tng">{t.t('dashboard.customers.paymentMethods.tng')}</option>
                  <option value="grabpay">
                    {t.t('dashboard.customers.paymentMethods.grabpay')}
                  </option>
                  <option value="card">{t.t('dashboard.customers.paymentMethods.card')}</option>
                  <option value="bank_transfer">
                    {t.t('dashboard.customers.paymentMethods.bank_transfer')}
                  </option>
                  <option value="voucher">
                    {t.t('dashboard.customers.paymentMethods.voucher')}
                  </option>
                  <option value="free">{t.t('dashboard.customers.paymentMethods.free')}</option>
                </select>
              </label>

              <label style={{ display: 'grid', gap: 4 }}>
                <span style={fieldLabel}>
                  {t.t('dashboard.customers.priceOverrideLabel', {
                    default: money.format(defaultPriceSen),
                  })}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder={`Leave blank to charge ${money.format(defaultPriceSen)}`}
                  value={priceRm}
                  onChange={(e) => setPriceRm(e.target.value)}
                  style={inputStyle}
                />
                {!priceLooksValid ? (
                  <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-accent)' }}>
                    {t.t('dashboard.customers.priceInvalid')}
                  </span>
                ) : null}
              </label>

              <div
                style={{
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgba(13,122,79,0.06)',
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface)',
                }}
              >
                <strong>{money.format(finalPriceSen)}</strong>{' '}
                {t.t('dashboard.customers.chargeSummary', {
                  method: paymentMethodLabel(t, method),
                  customer: customer.displayName,
                  package: picked ? describePackage(t, picked) : '—',
                })}
              </div>

              {status === 'error' && error ? (
                <p
                  role="alert"
                  style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
                >
                  {error}
                </p>
              ) : null}
              {status === 'issued' ? (
                <p
                  role="status"
                  style={{
                    margin: 0,
                    color: 'var(--color-primary)',
                    fontWeight: 500,
                    fontSize: 'var(--text-meta)',
                  }}
                >
                  {t.t('dashboard.customers.packIssuedClosing')}
                </p>
              ) : null}

              <div
                style={{
                  display: 'flex',
                  gap: 'var(--space-2)',
                  justifyContent: 'flex-end',
                  marginTop: 'var(--space-2)',
                }}
              >
                <button
                  type="button"
                  onClick={onClose}
                  disabled={status === 'issuing'}
                  style={ghostButtonStyle}
                >
                  {t.t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => void confirm()}
                  disabled={
                    !packageId || !priceLooksValid || status === 'issuing' || status === 'issued'
                  }
                  style={{
                    ...primaryButtonStyle,
                    opacity:
                      !packageId || !priceLooksValid || status === 'issuing' || status === 'issued'
                        ? 0.5
                        : 1,
                  }}
                >
                  {status === 'issuing'
                    ? t.t('dashboard.customers.issuing')
                    : status === 'issued'
                      ? t.t('dashboard.customers.issuedCheck')
                      : t.t('dashboard.customers.issuePackPrice', {
                          price: money.format(finalPriceSen),
                        })}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </ModalShell>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}) {
  const t = useI18n();
  // Esc-to-close. The backdrop also closes on click.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
        zIndex: 200,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-3)',
          padding: 'var(--space-5)',
          maxWidth: 480,
          width: '100%',
          maxHeight: '90dvh',
          overflowY: 'auto',
          display: 'grid',
          gap: 'var(--space-4)',
        }}
      >
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t.t('dashboard.customers.close')}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 18,
              cursor: 'pointer',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function describePackage(t: I18n, p: WashPackage): string {
  if (p.kind === 'unlimited') {
    return p.recurringMonths
      ? t.t('dashboard.customers.packUnlimitedCycle', { months: p.recurringMonths })
      : t.t('dashboard.customers.packUnlimited');
  }
  return t.t('dashboard.customers.packCount', {
    washes: p.washCount ?? 0,
    days: p.validityDays,
  });
}

function paymentMethodLabel(t: I18n, m: CustomerSubscription['paymentMethod']): string {
  return t.t(`dashboard.customers.paymentMethodsInline.${m}`);
}

function isLiveSubscription(s: CustomerSubscription): boolean {
  if (s.status !== 'active' && s.status !== 'paused') return false;
  const expiry = timestampToMs(s.expiresAt);
  if (expiry > 0 && expiry < Date.now()) return false;
  return true;
}

function parseRmToSenOrNull(rm: string): number | null {
  const trimmed = rm.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function extractFriendlySubscriptionError(t: I18n, err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = String((err as { message: string }).message);
    if (m.includes('failed-precondition') && m.includes('inactive')) {
      return t.t('dashboard.customers.errPackInactive');
    }
    if (m.includes('failed-precondition') && m.includes('no wash quota')) {
      return t.t('dashboard.customers.errPackNoQuota');
    }
    if (m.includes('permission-denied')) return t.t('dashboard.customers.errNoPermPacks');
    if (m.includes('not-found')) return t.t('dashboard.customers.errPackGone');
    return m;
  }
  return t.t('dashboard.customers.errActionFailed');
}

// ── Helpers + styles ───────────────────────────────────────────────────

function filterCustomers(
  list: readonly TenantCustomerIndex[],
  rawQuery: string,
): TenantCustomerIndex[] {
  const sorted = list
    .slice()
    .sort((a, b) => timestampToMs(b.lastVisitAt) - timestampToMs(a.lastVisitAt));
  const q = rawQuery.trim().toLowerCase();
  if (!q) return sorted;
  const isDigits = /^\+?\d+$/.test(rawQuery.trim());
  if (isDigits) {
    const digits = q.replace(/\D+/g, '');
    return sorted.filter((c) => c.phoneNormalised && c.phoneNormalised.startsWith(digits));
  }
  return sorted.filter((c) => {
    if (c.displayName.toLowerCase().includes(q)) return true;
    return c.platesLower.some((p) => p.includes(q));
  });
}

function isUsableTemplate(t: VoucherTemplate): boolean {
  if (!t.active) return false;
  const to = timestampToMs(t.validity.to);
  return to > Date.now();
}

function describeEffect(t: I18n, effect: VoucherTemplate['effect']): string {
  switch (effect.type) {
    case 'pct_off':
      return t.t('dashboard.customers.effectPctOff', { value: effect.value });
    case 'fixed_off':
      return t.t('dashboard.customers.effectFixedOff', { amount: money.format(effect.value) });
    case 'free_service':
      return t.t('dashboard.customers.effectFreeService');
    case 'free_package':
      return t.t('dashboard.customers.effectFreePackage');
  }
}

/**
 * Tier id, badge colour, and the tier's name in the reader's language.
 *
 * This used to read `found.name['en']`, so every operator saw "Bronze /
 * Silver / Gold" regardless of locale — even though `defaultTiers` ships all
 * four names (Gangsa / 青铜 / வெண்கலம்). The translations existed and were
 * being thrown away one line before they were rendered.
 *
 * The unknown-tier fallback now takes the lowest configured tier rather than a
 * hard-coded 'Bronze' literal, so it stays correct if the ladder is renamed.
 */
function tierMeta(
  tierId: string | undefined,
  locale: Locale,
): { id: string; label: string; colour: string } {
  const found = defaultTiers.find((t) => t.id === tierId) ?? defaultTiers[0];
  if (!found) return { id: 'bronze', colour: '#cd7f32', label: '' };
  return {
    id: found.id,
    label: found.name[locale] ?? found.name.en ?? found.id,
    colour: found.badge.colour,
  };
}

function timestampToMs(ts: { toMillis?: () => number; seconds: number } | undefined): number {
  if (!ts) return 0;
  return typeof ts.toMillis === 'function' ? ts.toMillis() : ts.seconds * 1000;
}

function formatRelativeFromMs(t: I18n, ms: number): string {
  if (!ms) return t.t('dashboard.customers.relNever');
  const diff = Date.now() - ms;
  if (diff < 0) return t.t('dashboard.customers.relJustNow');
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return t.t('dashboard.customers.relToday');
  if (diff < 2 * day) return t.t('dashboard.customers.relYesterday');
  if (diff < 30 * day) return t.t('dashboard.customers.relDaysAgo', { n: Math.floor(diff / day) });
  if (diff < 365 * day)
    return t.t('dashboard.customers.relMoAgo', { n: Math.floor(diff / (30 * day)) });
  return t.t('dashboard.customers.relYrAgo', { n: Math.floor(diff / (365 * day)) });
}

function formatDateFromMs(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

function formatPhone(digits: string): string {
  if (digits.length < 4) return digits;
  // Lightly format MY-style: 0123456789 → 012-345 6789. Best-effort, no full localisation.
  return digits.replace(/^(\d{3})(\d{3})(\d+)$/, '$1-$2 $3');
}

function extractFriendlyError(t: I18n, err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = String((err as { message: string }).message);
    if (m.includes('failed-precondition')) return t.t('dashboard.customers.errVoucherMax');
    if (m.includes('permission-denied')) return t.t('dashboard.customers.errNoPermVoucher');
    if (m.includes('not-found')) return t.t('dashboard.customers.errTemplateGone');
    return m;
  }
  return t.t('dashboard.customers.errIssueFailed');
}

function TierBadge({ tier }: { readonly tier: { label: string; colour: string } }) {
  return (
    <span
      style={{
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        background: `${tier.colour}1f`,
        color: tier.colour,
        fontSize: 'var(--text-meta)',
        fontWeight: 500,
      }}
    >
      {tier.label}
    </span>
  );
}

function Stat({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          fontSize: 'var(--text-body)',
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </dd>
    </div>
  );
}

function EmptyState({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: { label: string; onClick?: () => void; to?: string };
}) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: 'var(--space-6) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-3)',
        justifyItems: 'center',
      }}
    >
      <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          maxWidth: 360,
        }}
      >
        {body}
      </p>
      {cta?.to ? (
        <Link
          to={cta.to}
          style={{ ...primaryButtonStyle, textDecoration: 'none', display: 'inline-flex' }}
        >
          {cta.label}
        </Link>
      ) : cta?.onClick ? (
        <button type="button" onClick={cta.onClick} style={ghostButtonStyle}>
          {cta.label}
        </button>
      ) : null}
    </div>
  );
}

function DeadEndScreen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta: { label: string; to: string };
}) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center' }}>
        <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            maxWidth: 360,
          }}
        >
          {body}
        </p>
        <Link
          to={cta.to}
          style={{ ...primaryButtonStyle, textDecoration: 'none', display: 'inline-flex' }}
        >
          {cta.label}
        </Link>
      </div>
    </main>
  );
}

function InlineNote({
  children,
  tone = 'muted',
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        margin: 0,
        padding: 'var(--space-3) var(--space-4)',
        fontSize: 'var(--text-meta)',
        color: tone === 'error' ? 'var(--color-accent)' : 'var(--color-on-surface-muted)',
        background: tone === 'error' ? 'rgba(239,68,68,0.06)' : 'var(--tint-subtle)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {children}
    </p>
  );
}

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const tinyGhostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '6px 10px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
};

const statGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--space-3) var(--space-4)',
  margin: 0,
};
